/**
 * HUD strings (spec §F copy deck). Calm and specific: no exclamation marks,
 * no urgency, no superlatives, no promises about future infrastructure.
 * Keys mirror the deck so a string table can replace this file later
 * (Marathi and Hindi). Where §F has no line (the About sheet beyond
 * about.*), the copy is written here in the same voice.
 */
export const HUD_COPY = {
  /* light */
  lightAria: "Map light", //                                   light.aria

  /* zoom island + mobile map buttons */
  zoomIn: "Zoom in", //                                         zoom.in
  zoomOut: "Zoom out", //                                       zoom.out
  home: "Back to the site", //                                  zoom.home
  layers: "Layers", //                                          zoom.layers
  mapView: "Map view",
  /** The landmark around the islands (zoom, map buttons, layers, tools), so landmark navigation finds them. */
  mapControls: "Map controls",

  /* tools island */
  tools: "View tools",
  share: "Share this view", //                                  view.share
  fullscreen: "Full screen", //                                 view.fullscreen
  exitFullscreen: "Exit full screen", //                        view.exit
  copied: "Link copied", //                                     share.copied
  copiedAnnounce: "Link copied.", //                            §H.3 Share
  copyFailed: "The link couldn't be copied. Select it below.", // share.failed
  linkLabel: "Link to this view",
  dismiss: "Dismiss",

  /* layers card (layers.*) */
  layersTitle: "Layers", //                                     layers.title
  layerLabels: {
    routes: "Drive routes",
    future: "Future infrastructure",
    contours: "Contours",
    hillshade: "Terrain shading",
    rings: "Distance rings",
    boundary: "Site outline",
    labels: "Place names",
  },
  boundaryNote: "Approximate", //                               layers.boundary sublabel
  hillshadeWaiting: "Arrives after you enter",
  layersFooter: "Planned roads are indicative.",

  /* attribution */
  attribution: "© OpenStreetMap contributors · Protomaps · © Mapterhorn (Copernicus DEM)", // attribution
  attributionShort: "© OpenStreetMap · Protomaps · Mapterhorn", // OSMF: attribution names "OpenStreetMap" (QA L3-4)
  attributionName: "Map data sources",
  poweredBy: "Powered by", //                                   powered
  poweredName: "Flux Real",
  about: "About this map", //                                   about.button / about.title

  /* about sheet */
  aboutKicker: "Ládera City · Lohegaon, Pune",
  aboutLead: "Where the numbers and lines on this map come from.",
  aboutClose: "Close",
  sectionTimes: "Drive times",
  sectionFuture: "Future infrastructure",
  sectionSite: "Site outline",
  sectionPins: "Place pins",
  sectionData: "Data licences", //                              attribution.licence
  /** about.times */
  times: (date: string) =>
    `Drive times are off-peak estimates by road from the site's access road, calculated from OpenStreetMap data dated ${date}. Traffic changes them.`,
  timesUndated:
    "Drive times are off-peak estimates by road from the site's access road, calculated from OpenStreetMap data. Traffic changes them.",
  offPeak:
    "Off-peak means clear roads: each time assumes traffic moves freely at the speeds the roads allow. At busy hours the same drive takes longer.",
  distances: "Distances are measured along the road, not in a straight line.",
  advised: "Where Indulkar has advised a drive time, the place card says so.",
  /** about.future (S5) */
  future:
    "Planned roads come from OpenStreetMap, the Ládera City master-plan drawings and published development-plan references. Where no alignment is published, the line is schematic. Every planned alignment is indicative and subject to approvals.",
  /**
   * Plain-language source lines, keyed by future.geojson corridor id. Each one
   * says what the feature's `source` field says; unknown ids fall back to it.
   */
  futureSources: {
    "outer-ring-road": "OpenStreetMap, the Pune Ring Road. Some sections are still mapped as proposed",
    "inner-ring-road": "Schematic, drawn through the connection points marked by Indulkar. It is not the surveyed PMRDA alignment",
    "lohegaon-corridors": "The Ládera City master plan, page 7: possible development-plan roads",
    "irr-orr-connection":
      "PMC draft development plan, sheet 3, up to the PMC limit. Beyond it the line follows existing roads to the Pune Ring Road",
  } as Readonly<Record<string, string>>,
  /** No date: the data's sourceDate is the day it was prepared, not the document's date (QA L3-8). */
  futureSource: (source: string) => `${source}.`,
  /** about.site */
  site: "The site outline is approximate, traced from the master plan.",
  pins:
    "Place pins start from the Google Maps links provided by Indulkar. Where the same place is mapped in OpenStreetMap close by, the pin sits on that feature. A few places without a link were located from their address or placed by hand.",
  /** about.data */
  data: "Map data © OpenStreetMap contributors, available under the Open Database License. Terrain © Mapterhorn, from the Copernicus DEM.",
  derived:
    "Drive routes and road corridors are derived from OpenStreetMap data, so the same licence covers them. The basemap is built with Protomaps.",
  links: [
    { label: "OpenStreetMap copyright and licence", href: "https://www.openstreetmap.org/copyright" },
    { label: "Protomaps", href: "https://protomaps.com" },
    { label: "Mapterhorn", href: "https://mapterhorn.com" },
  ],
  newTab: "(opens in a new tab)",
  prepared: (date: string) => `Data prepared ${date}`,
} as const;

/** "{n} pins as supplied · …" — the place-pin provenance line in the About sheet. */
export function pinsBreakdown(c: { clientPin: number; osm: number; geocode: number; manual: number }): string {
  const parts: string[] = [];
  if (c.clientPin) parts.push(`${c.clientPin} ${c.clientPin === 1 ? "pin" : "pins"} as supplied`);
  if (c.osm) parts.push(`${c.osm} on OpenStreetMap features`);
  if (c.geocode) parts.push(`${c.geocode} from an address`);
  if (c.manual) parts.push(`${c.manual} placed by hand`);
  return parts.length ? `${parts.join(" · ")}.` : "";
}
